import {
  UserServiceClient,
  type ConfirmSignupRequest,
  type ConfirmSignupResponse,
  type CodeDeliveryResponse,
  type RequestOptions,
  type ResendConfirmationRequest,
  type SignupResponse,
} from "@its-engineering-and-development/user-service-client";
import { userServiceApiKey, userServiceUrl } from "@app/lib/auth/auth-config";

export type EventsAdminSignupRequest = {
  email: string;
  name: string;
};

// Lazy singleton: the underlying client validates `apiKey` in its constructor
// and throws on an empty value. Constructing eagerly at module-import time
// would break any test that transitively imports this module without setting
// VITE_USER_SERVICE_API_KEY. Defer until the first real call.
let cachedClient: UserServiceClient | null = null;
const REQUEST_TIMEOUT_MS = 10_000;

function getClient(): UserServiceClient {
  if (!cachedClient) {
    cachedClient = new UserServiceClient({
      baseUrl: userServiceUrl,
      apiKey: userServiceApiKey,
    });
  }
  return cachedClient;
}

function withRequestDeadline(opts?: RequestOptions): RequestOptions {
  const timeoutSignal = AbortSignal.timeout(REQUEST_TIMEOUT_MS);

  return {
    ...opts,
    signal: opts?.signal
      ? AbortSignal.any([opts.signal, timeoutSignal])
      : timeoutSignal,
  };
}

export const eventsAdminUserServiceClient = {
  signup(
    input: EventsAdminSignupRequest,
    opts?: RequestOptions,
  ): Promise<SignupResponse> {
    // Keep the package-contract drift isolated at this adapter boundary until
    // the published SDK catches up with the deployed email+name signup shape.
    return getClient().signup(
      input as unknown as Parameters<UserServiceClient["signup"]>[0],
      withRequestDeadline(opts),
    );
  },
  confirmSignup(
    input: ConfirmSignupRequest,
    opts?: RequestOptions,
  ): Promise<ConfirmSignupResponse> {
    return getClient().confirmSignup(input, withRequestDeadline(opts));
  },
  resendConfirmation(
    input: ResendConfirmationRequest,
    opts?: RequestOptions,
  ): Promise<CodeDeliveryResponse> {
    return getClient().resendConfirmation(input, withRequestDeadline(opts));
  },
};
